/**
 * Admin · Calendar — full Tabulex-style CMS for events.
 *
 * Lets the director: create/edit/delete events with every field
 * (group, parent visibility, permission slip, attachments, reminders),
 * filter by group, export ICS, export CSV.
 */
import { useEffect, useMemo, useState } from "react";
import {
  CalendarDays,
  Download,
  Pencil,
  Plus,
  Trash2,
  Sparkles,
  MapPin,
  Users as UsersIcon,
  LayoutGrid,
  List,
} from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { useAdminWorkspace, AdminHeader, AdminEmpty } from "./_shared";
import CalendarMonthGrid from "../../components/CalendarMonthGrid";

const KIND_OPTIONS = [
  { value: "info", label: "Note" },
  { value: "field_trip", label: "Field trip" },
  { value: "parent_meeting", label: "Parent meeting" },
  { value: "holiday", label: "Holiday closure" },
  { value: "closure", label: "Closure" },
  { value: "celebration", label: "Celebration" },
  { value: "activity", label: "Activity" },
  { value: "staff_meeting", label: "Staff meeting" },
  { value: "training", label: "Training" },
];

const KIND_LABEL = Object.fromEntries(KIND_OPTIONS.map((o) => [o.value, o.label]));

const KIND_COLOR = {
  info: "#9BA995",
  field_trip: "#8A9984",
  parent_meeting: "#A89CC8",
  holiday: "#E6BFA6",
  closure: "#D4A85F",
  celebration: "#FFB800",
  activity: "#1A1B2E",
  staff_meeting: "#1F3A2A",
  training: "#7E8C72",
};

function fmtWhen(iso) {
  if (!iso) return "—";
  const isDate = /^\d{4}-\d{2}-\d{2}$/.test(iso);
  const d = new Date(iso);
  if (isDate) {
    return d.toLocaleDateString(undefined, {
      weekday: "short",
      month: "short",
      day: "numeric",
    });
  }
  return d.toLocaleString(undefined, {
    weekday: "short",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

const EMPTY_FORM = {
  title: "",
  body: "",
  kind: "info",
  starts_at: "",
  ends_at: "",
  location: "",
  rsvp_required: false,
  group_id: "",
  permission_slip_required: false,
  parent_visible: true,
  attachments: "",
  reminders: ["1d"],
};

function EventComposer({ initial, onCancel, onSaved, wsId }) {
  const [form, setForm] = useState({ ...EMPTY_FORM, ...(initial || {}) });
  const [saving, setSaving] = useState(false);

  const toggleReminder = (r) =>
    setForm((f) => ({
      ...f,
      reminders: f.reminders.includes(r)
        ? f.reminders.filter((x) => x !== r)
        : [...f.reminders, r],
    }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title.trim() || !form.starts_at) {
      toast("Please add a title and a start.");
      return;
    }
    setSaving(true);
    try {
      const body = {
        title: form.title.trim(),
        body: form.body.trim() || null,
        kind: form.kind,
        starts_at: form.starts_at,
        ends_at: form.ends_at || null,
        location: form.location.trim() || null,
        rsvp_required: !!form.rsvp_required,
        group_id: form.group_id.trim() || null,
        permission_slip_required: !!form.permission_slip_required,
        parent_visible: !!form.parent_visible,
        attachments: form.attachments
          .split("\n")
          .map((s) => s.trim())
          .filter(Boolean),
        reminders: form.reminders,
      };
      if (initial?.id) {
        await api.patch(`/events/${initial.id}`, body);
        toast("Event quietly updated.");
      } else {
        await api.post(`/workspaces/${wsId}/events`, body);
        toast("Event added to the calendar.");
      }
      onSaved();
    } catch (err) {
      toast(err.response?.data?.detail || "Couldn't save the event.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <form
      data-testid="admin-event-composer"
      onSubmit={submit}
      className="rounded-3xl bg-white border hairline p-6 space-y-5 mt-6"
    >
      <h3 className="font-serif text-xl text-[#171717]">
        {initial?.id ? "Edit event" : "Add a new event"}
      </h3>
      <div className="grid sm:grid-cols-2 gap-4">
        <div className="sm:col-span-2">
          <label className="eyebrow">Title</label>
          <input
            type="text"
            value={form.title}
            onChange={(e) => setForm({ ...form, title: e.target.value })}
            data-testid="admin-event-title"
            placeholder="Apple orchard trip"
            className="orbit-input mt-2"
          />
        </div>
        <div>
          <label className="eyebrow">Kind</label>
          <select
            value={form.kind}
            onChange={(e) => setForm({ ...form, kind: e.target.value })}
            data-testid="admin-event-kind"
            className="orbit-input mt-2 bg-transparent"
          >
            {KIND_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="eyebrow">Group / class</label>
          <input
            type="text"
            value={form.group_id}
            onChange={(e) => setForm({ ...form, group_id: e.target.value })}
            data-testid="admin-event-group"
            placeholder="Sunflower Room"
            className="orbit-input mt-2"
          />
        </div>
        <div>
          <label className="eyebrow">Starts at</label>
          <input
            type="datetime-local"
            value={form.starts_at}
            onChange={(e) => setForm({ ...form, starts_at: e.target.value })}
            data-testid="admin-event-starts"
            className="orbit-input mt-2"
          />
        </div>
        <div>
          <label className="eyebrow">Ends at (optional)</label>
          <input
            type="datetime-local"
            value={form.ends_at}
            onChange={(e) => setForm({ ...form, ends_at: e.target.value })}
            data-testid="admin-event-ends"
            className="orbit-input mt-2"
          />
        </div>
        <div className="sm:col-span-2">
          <label className="eyebrow">Location</label>
          <input
            type="text"
            value={form.location}
            onChange={(e) => setForm({ ...form, location: e.target.value })}
            data-testid="admin-event-location"
            placeholder="Riverbend Orchard"
            className="orbit-input mt-2"
          />
        </div>
        <div className="sm:col-span-2">
          <label className="eyebrow">Description</label>
          <textarea
            value={form.body}
            onChange={(e) => setForm({ ...form, body: e.target.value })}
            data-testid="admin-event-body"
            rows={3}
            placeholder="Bring a hat and water bottle. Departure 9 sharp."
            className="orbit-input mt-2"
          />
        </div>
        <div className="sm:col-span-2">
          <label className="eyebrow">Attachments (one URL per line)</label>
          <textarea
            value={form.attachments}
            onChange={(e) => setForm({ ...form, attachments: e.target.value })}
            data-testid="admin-event-attachments"
            rows={2}
            placeholder="https://…/itinerary.pdf"
            className="orbit-input mt-2"
          />
        </div>
        <div className="sm:col-span-2 flex flex-wrap gap-4 pt-2">
          <label className="inline-flex items-center gap-2 text-sm text-[#2C2B29]">
            <input
              type="checkbox"
              checked={form.parent_visible}
              onChange={(e) =>
                setForm({ ...form, parent_visible: e.target.checked })
              }
              data-testid="admin-event-parent-visible"
            />
            Visible to parents
          </label>
          <label className="inline-flex items-center gap-2 text-sm text-[#2C2B29]">
            <input
              type="checkbox"
              checked={form.rsvp_required}
              onChange={(e) =>
                setForm({ ...form, rsvp_required: e.target.checked })
              }
              data-testid="admin-event-rsvp-required"
            />
            RSVP required
          </label>
          <label className="inline-flex items-center gap-2 text-sm text-[#2C2B29]">
            <input
              type="checkbox"
              checked={form.permission_slip_required}
              onChange={(e) =>
                setForm({
                  ...form,
                  permission_slip_required: e.target.checked,
                })
              }
              data-testid="admin-event-permission-slip"
            />
            Permission slip required
          </label>
        </div>
        <div className="sm:col-span-2">
          <span className="eyebrow">Send a gentle reminder</span>
          <div className="mt-2 flex flex-wrap gap-2">
            {[
              { v: "1d", label: "1 day before" },
              { v: "2h", label: "2 hours before" },
              { v: "1h", label: "1 hour before" },
            ].map((r) => (
              <button
                key={r.v}
                type="button"
                onClick={() => toggleReminder(r.v)}
                data-testid={`admin-event-reminder-${r.v}`}
                className={`rounded-full px-4 py-2 text-xs tracking-[0.16em] uppercase font-medium transition-colors ${
                  form.reminders.includes(r.v)
                    ? "bg-[#132619] text-white"
                    : "bg-[#F2EFEA] text-[#525252] hover:bg-[#EAE5EF]"
                }`}
              >
                {r.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="flex justify-end gap-3 pt-2">
        <button
          type="button"
          onClick={onCancel}
          data-testid="admin-event-cancel"
          className="btn-ghost !py-2 !px-5 !text-xs"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={saving}
          data-testid="admin-event-save"
          className="btn-primary !py-2 !px-5 !text-xs"
        >
          {saving ? "Saving…" : initial?.id ? "Save changes" : "Add to calendar"}
        </button>
      </div>
    </form>
  );
}

export default function AdminCalendar() {
  const { workspace, loading: wsLoading } = useAdminWorkspace();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [composerOpen, setComposerOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [groupFilter, setGroupFilter] = useState("");
  const [view, setView] = useState("month"); // month | list

  const reload = async () => {
    if (!workspace) return;
    setLoading(true);
    try {
      const params = new URLSearchParams({ limit: "200" });
      if (groupFilter) params.set("group_id", groupFilter);
      const r = await api.get(
        `/workspaces/${workspace.id}/events?${params.toString()}`
      );
      setEvents(r.data || []);
    } catch {
      setEvents([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    reload();
  }, [workspace?.id, groupFilter]);

  const groups = useMemo(() => {
    const s = new Set();
    events.forEach((e) => e.group_id && s.add(e.group_id));
    return [...s].sort();
  }, [events]);

  const downloadIcs = async () => {
    try {
      const res = await api.get(
        `/admin/workspaces/${workspace.id}/events.ics?days=90`,
        { responseType: "blob" }
      );
      const blob = new Blob([res.data], { type: "text/calendar" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `alouvya-calendar-${workspace.name.replace(/\s+/g, "-")}.ics`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      /* quiet */
    }
  };

  const downloadCsv = async () => {
    try {
      const res = await api.get(
        `/admin/workspaces/${workspace.id}/events.csv?days=90`,
        { responseType: "blob" }
      );
      const blob = new Blob([res.data], { type: "text/csv" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `alouvya-calendar-${workspace.name.replace(/\s+/g, "-")}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      /* quiet */
    }
  };

  const remove = async (ev) => {
    if (!confirm(`Remove "${ev.title}" from the calendar?`)) return;
    try {
      await api.delete(`/events/${ev.id}`);
      toast("Event quietly removed.");
      reload();
    } catch {
      toast("Couldn't remove the event.");
    }
  };

  if (wsLoading) return null;
  if (!workspace)
    return (
      <AdminEmpty testid="admin-calendar-no-ws">No workspace yet.</AdminEmpty>
    );

  return (
    <div data-testid="admin-calendar-screen">
      <AdminHeader
        eyebrow="Calendar"
        title="Plan the days gently"
      />
      <div className="mt-2 text-sm text-[#525252] max-w-2xl">
        Schedule field trips, parent meetings, closures, and class activities.
        Toggle parent visibility per event — keep planning whispers internal.
      </div>

      <div className="mt-6 flex flex-wrap items-center gap-3">
        <div className="inline-flex bg-[#FAF6EE] rounded-full p-1">
          <button
            type="button"
            data-testid="admin-calendar-view-month"
            onClick={() => setView("month")}
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
              view === "month" ? "bg-white shadow text-[#171717]" : "text-[#525252]"
            }`}
          >
            <LayoutGrid size={12} strokeWidth={1.6} />
            Month
          </button>
          <button
            type="button"
            data-testid="admin-calendar-view-list"
            onClick={() => setView("list")}
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
              view === "list" ? "bg-white shadow text-[#171717]" : "text-[#525252]"
            }`}
          >
            <List size={12} strokeWidth={1.6} />
            List
          </button>
        </div>
        <button
          type="button"
          onClick={() => {
            setEditing(null);
            setComposerOpen((v) => !v);
          }}
          data-testid="admin-calendar-add"
          className="inline-flex items-center gap-2 rounded-full px-4 py-2 bg-[#132619] text-white text-xs tracking-[0.16em] uppercase font-medium hover:bg-[#0b2117] transition-colors"
        >
          <Plus size={13} strokeWidth={1.8} />
          New event
        </button>
        {groups.length > 0 && (
          <div className="inline-flex items-center gap-2 bg-white border hairline rounded-full px-4 py-2">
            <label className="text-[11px] tracking-[0.18em] uppercase text-[#525252]">
              Group
            </label>
            <select
              value={groupFilter}
              onChange={(e) => setGroupFilter(e.target.value)}
              data-testid="admin-calendar-group-filter"
              className="bg-transparent outline-none text-sm font-medium"
            >
              <option value="">All</option>
              {groups.map((g) => (
                <option key={g} value={g}>
                  {g}
                </option>
              ))}
            </select>
          </div>
        )}
        <button
          type="button"
          onClick={downloadIcs}
          data-testid="admin-calendar-download-ics"
          className="inline-flex items-center gap-2 rounded-full px-4 py-2 bg-white border hairline text-xs tracking-[0.16em] uppercase font-medium text-[#525252] hover:bg-[#F2EFEA] transition-colors"
        >
          <Download size={13} strokeWidth={1.7} />
          Download .ics
        </button>
        <button
          type="button"
          onClick={downloadCsv}
          data-testid="admin-calendar-download-csv"
          className="inline-flex items-center gap-2 rounded-full px-4 py-2 bg-white border hairline text-xs tracking-[0.16em] uppercase font-medium text-[#525252] hover:bg-[#F2EFEA] transition-colors"
        >
          <Download size={13} strokeWidth={1.7} />
          Download .csv
        </button>
      </div>

      {composerOpen && (
        <EventComposer
          initial={editing}
          wsId={workspace.id}
          onCancel={() => {
            setComposerOpen(false);
            setEditing(null);
          }}
          onSaved={() => {
            setComposerOpen(false);
            setEditing(null);
            reload();
          }}
        />
      )}

      {loading ? (
        <AdminEmpty testid="admin-calendar-loading">A quiet moment…</AdminEmpty>
      ) : view === "month" ? (
        <div className="mt-6">
          <CalendarMonthGrid
            events={events}
            kindColors={KIND_COLOR}
            onPick={(ev) => {
              setEditing({
                id: ev.id,
                title: ev.title,
                body: ev.body || "",
                kind: ev.kind,
                starts_at: ev.starts_at?.slice(0, 16) || "",
                ends_at: ev.ends_at?.slice(0, 16) || "",
                location: ev.location || "",
                rsvp_required: !!ev.rsvp_required,
                group_id: ev.group_id || "",
                permission_slip_required: !!ev.permission_slip_required,
                parent_visible: ev.parent_visible !== false,
                attachments: (ev.attachments || []).join("\n"),
                reminders: ev.reminders || [],
              });
              setComposerOpen(true);
            }}
          />
        </div>
      ) : events.length === 0 ? (
        <AdminEmpty testid="admin-calendar-empty">
          Nothing on the calendar yet. Press <em>New event</em> to add the first one.
        </AdminEmpty>
      ) : (
        <ul
          data-testid="admin-calendar-list"
          className="mt-6 rounded-3xl bg-white border hairline overflow-hidden divide-y divide-[#171717]/6"
        >
          {events.map((ev) => (
            <li
              key={ev.id}
              data-testid={`admin-calendar-row-${ev.id}`}
              className="px-5 py-4 flex items-start gap-4"
            >
              <span
                aria-hidden
                className="h-10 w-10 rounded-2xl flex items-center justify-center shrink-0 mt-0.5"
                style={{ background: `${KIND_COLOR[ev.kind] || "#9BA995"}20` }}
              >
                <CalendarDays
                  size={14}
                  strokeWidth={1.6}
                  style={{ color: KIND_COLOR[ev.kind] || "#9BA995" }}
                />
              </span>
              <div className="flex-1 min-w-0">
                <div className="flex items-baseline flex-wrap gap-2">
                  <span className="font-serif text-base text-[#171717]">
                    {ev.title}
                  </span>
                  <span
                    className="text-[10px] tracking-[0.18em] uppercase px-2 py-0.5 rounded-full"
                    style={{
                      background: `${KIND_COLOR[ev.kind] || "#9BA995"}20`,
                      color: KIND_COLOR[ev.kind] || "#9BA995",
                    }}
                  >
                    {KIND_LABEL[ev.kind] || ev.kind}
                  </span>
                  {!ev.parent_visible && (
                    <span className="text-[10px] tracking-[0.18em] uppercase px-2 py-0.5 rounded-full bg-[#1F3A2A]/15 text-[#1F3A2A]">
                      Staff only
                    </span>
                  )}
                  {ev.permission_slip_required && (
                    <span className="text-[10px] tracking-[0.18em] uppercase px-2 py-0.5 rounded-full bg-[#D4A85F]/20 text-[#9C7826]">
                      Slip · {ev.permission_slips_count}
                    </span>
                  )}
                  {ev.rsvp_required && (
                    <span className="text-[10px] tracking-[0.18em] uppercase px-2 py-0.5 rounded-full bg-[#A89CC8]/20 text-[#1F3A2A]">
                      RSVP · {ev.rsvp_going} going
                    </span>
                  )}
                </div>
                <div className="text-xs text-[#525252] mt-1 flex flex-wrap gap-3">
                  <span>{fmtWhen(ev.starts_at)}</span>
                  {ev.location && (
                    <span className="inline-flex items-center gap-1">
                      <MapPin size={11} strokeWidth={1.6} />
                      {ev.location}
                    </span>
                  )}
                  {ev.group_id && (
                    <span className="inline-flex items-center gap-1">
                      <UsersIcon size={11} strokeWidth={1.6} />
                      {ev.group_id}
                    </span>
                  )}
                  {ev.reminders?.length > 0 && (
                    <span className="inline-flex items-center gap-1">
                      <Sparkles size={11} strokeWidth={1.6} />
                      {ev.reminders.join(" · ")}
                    </span>
                  )}
                </div>
                {ev.body && (
                  <div className="text-xs text-[#525252] mt-1 italic">
                    {ev.body}
                  </div>
                )}
              </div>
              <div className="shrink-0 flex gap-1">
                <button
                  type="button"
                  onClick={() => {
                    setEditing({
                      id: ev.id,
                      title: ev.title,
                      body: ev.body || "",
                      kind: ev.kind,
                      starts_at: ev.starts_at?.slice(0, 16) || "",
                      ends_at: ev.ends_at?.slice(0, 16) || "",
                      location: ev.location || "",
                      rsvp_required: !!ev.rsvp_required,
                      group_id: ev.group_id || "",
                      permission_slip_required: !!ev.permission_slip_required,
                      parent_visible: ev.parent_visible !== false,
                      attachments: (ev.attachments || []).join("\n"),
                      reminders: ev.reminders || [],
                    });
                    setComposerOpen(true);
                  }}
                  data-testid={`admin-calendar-edit-${ev.id}`}
                  className="h-8 w-8 rounded-full bg-[#F2EFEA] hover:bg-[#EAE5EF] flex items-center justify-center text-[#525252]"
                  aria-label="Edit"
                  title="Edit"
                >
                  <Pencil size={13} strokeWidth={1.6} />
                </button>
                <button
                  type="button"
                  onClick={() => remove(ev)}
                  data-testid={`admin-calendar-delete-${ev.id}`}
                  className="h-8 w-8 rounded-full bg-[#F2EFEA] hover:bg-[#FFB800]/30 flex items-center justify-center text-[#525252]"
                  aria-label="Delete"
                  title="Delete"
                >
                  <Trash2 size={13} strokeWidth={1.6} />
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
